/**
 * @fileoverview Audio Recording Service
 * Handles microphone recording, a live waveform tap, silence/max auto-stop, and
 * transcription upload (`gpt-4o-transcribe` on the backend).
 *
 * States: idle -> recording -> processing -> idle
 *
 * Lifecycle contract (see chat-input-redesign-phase1-core.md, Workstream B):
 * - One per-recording {@link AudioContext} + {@link AnalyserNode} tapped off the mic
 *   stream (never connected to `destination`), used for both the visual waveform
 *   ({@link AudioRecordingService#getAmplitude}) and silence detection.
 * - All live resources (stream tracks, AudioContext, nodes) AND both auto-stop timers
 *   are released **synchronously** when leaving the `recording` state, before the
 *   transcription network round-trip — so the OS mic indicator never stays lit during
 *   transcription (a privacy must in a therapy app).
 * - The stop path is a single guarded entry: a call while not `recording` is a silent
 *   no-op, so a late timer cannot tear down an in-flight transcription.
 * - No held-blob retry: a transcription failure discards the audio and returns to idle.
 *
 * Events emitted on the bus:
 * - `audio:state-changed` `{ state }` — drives the mic button + waveform UI lifecycle.
 * - `audio:quota-changed` `{ available, message, data }` — voice quota updates.
 * - `audio:auto-stop` `{ text, trigger }` — a silence/max auto-stop produced a
 *   transcript (UI appends it, same as a manual Stop; `text` may be empty).
 * - `audio:auto-stop-failed` `{ message, trigger }` — an auto-stop transcription failed.
 *
 * @module services/AudioRecordingService
 */

import { API_URL, fetchWithTimeout } from '../core/api.js';

/** @typedef {'idle' | 'recording' | 'processing'} AudioState */
/** @typedef {'manual' | 'silence' | 'max'} StopTrigger */

/** FFT size for the analyser (power of two; small = cheap, responsive bars). */
const FFT_SIZE = 128;
/** Hard recording cap (ms). Tunable. */
const MAX_DURATION_MS = 5 * 60 * 1000;
/** Sustained-silence window before auto-stop (ms). Tunable. */
const SILENCE_TIMEOUT_MS = 10 * 1000;
/** How often the silence detector samples RMS (ms). */
const SILENCE_SAMPLE_MS = 250;
/** RMS at/above which we count the user as speaking (onset). Tunable. */
const SILENCE_RMS_THRESHOLD = 0.02;
/**
 * Lower RMS that still resets the silence timer **once speaking has started**
 * (hysteresis: guards against noise-suppression attenuating soft speech below the
 * onset threshold and triggering a false silence auto-stop mid-sentence).
 */
const SILENCE_RMS_RELEASE = 0.012;
/** Transcription request timeout (ms). */
const TRANSCRIBE_TIMEOUT_MS = 60 * 1000;

export class AudioRecordingService {
  /**
   * @param {EventBus} eventBus - Event bus for state change notifications
   */
  constructor(eventBus) {
    this.eventBus = eventBus;
    /** @type {MediaRecorder|null} */
    this.mediaRecorder = null;
    /** @type {Blob[]} */
    this.audioChunks = [];
    /** @type {MediaStream|null} */
    this.stream = null;
    /** @type {AudioState} */
    this.state = 'idle';
    this.recordingStartedAt = null;
    this.quota = {
      available: true,
      message: null,
      data: null
    };

    // Live waveform / silence-detection audio graph (per-recording).
    /** @type {AudioContext|null} */
    this.audioContext = null;
    /** @type {AnalyserNode|null} */
    this.analyser = null;
    /** @type {MediaStreamAudioSourceNode|null} */
    this.sourceNode = null;

    // Service-owned auto-stop timers.
    this._maxTimer = null;
    this._silenceTimer = null;
    this._lastLoudAt = 0;
    this._speaking = false;

    // Transcription abort + cancel coordination.
    /** @type {AbortController|null} */
    this._abortController = null;
    this._cancelled = false;

    // Detect best supported audio format (Safari <18.4 needs mp4)
    this.mimeType = this.detectMimeType();
    this.refreshQuotaStatus();
  }

  /**
   * Detect the best supported MIME type for audio recording
   * @returns {string} Supported MIME type
   * @private
   */
  detectMimeType() {
    const formats = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/ogg;codecs=opus'
    ];
    for (const format of formats) {
      if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(format)) {
        return format;
      }
    }
    return 'audio/webm'; // Final fallback
  }

  /**
   * Check if audio recording is supported in this browser
   * @returns {boolean}
   */
  isSupported() {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
  }

  /**
   * Check whether recording is supported and currently allowed by quota.
   * @returns {boolean}
   */
  canRecord() {
    return this.isSupported() && this.quota.available;
  }

  /**
   * Get the latest voice transcription quota state.
   * @returns {{available: boolean, message: string|null, data: Object|null}}
   */
  getQuota() {
    return this.quota;
  }

  /**
   * Get current recording state
   * @returns {AudioState}
   */
  getState() {
    return this.state;
  }

  /**
   * Current microphone amplitude for the recording waveform: a single time-domain
   * RMS magnitude in `[0, 1]` (0 when idle / torn down, so a late rAF read can never
   * throw). The waveform UI injects this at the CENTER of a symmetric meter and
   * ripples it outward toward both edges (see Input._waveformLoop).
   * @returns {number}
   */
  getAmplitude() {
    if (this.state !== 'recording') return 0;
    return this._currentRms();
  }

  /**
   * Start recording audio from microphone
   * @returns {Promise<void>}
   * @throws {Error} If already recording, not supported, or permission denied
   */
  async startRecording() {
    if (this.state !== 'idle') {
      throw new Error('Already recording or processing');
    }

    if (!this.isSupported()) {
      throw new Error('Audio recording not supported in this browser');
    }

    if (!this.quota.available) {
      throw new Error(this.quota.message || 'Voice transcription limit reached. Try again later.');
    }

    this._cancelled = false;

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000
        }
      });

      await this._setupAudioGraph(this.stream);

      this.mediaRecorder = new MediaRecorder(this.stream, {
        mimeType: this.mimeType
      });

      this.audioChunks = [];

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.start(1000); // Collect data every second
      this.recordingStartedAt = Date.now();

      this.state = 'recording';
      this.emitStateChange();

      this._startTimers();

    } catch (error) {
      this._releaseAudioResources();
      this.state = 'idle';

      if (error.name === 'NotAllowedError') {
        throw new Error('Microphone access denied. Please allow microphone access and try again.');
      }
      if (error.name === 'AudioContextError') {
        throw new Error('Couldn\'t access the microphone. Please try again.');
      }
      throw new Error('Failed to start recording');
    }
  }

  /**
   * Stop recording, transcribe, and return the transcript to the caller (manual
   * Stop / Submit-during-recording). A call while not `recording` is a silent no-op
   * (returns `''`) so a stray late call can't tear down in-flight state.
   * @returns {Promise<string>} Transcribed text (empty string if no speech / no-op)
   * @throws {Error} On transcription failure (caller surfaces the notice; no retry)
   */
  async stopRecording() {
    return this._stopAndTranscribe({ trigger: 'manual', deliverViaEvent: false });
  }

  /**
   * Stop + transcribe sequence. Releases all live resources immediately, then awaits
   * transcription. See the class-level lifecycle contract.
   * @param {Object} cfg
   * @param {StopTrigger} cfg.trigger - what initiated the stop
   * @param {boolean} cfg.deliverViaEvent - true for service-initiated auto-stops
   *   (deliver the transcript via `audio:auto-stop`); false for caller-awaited stops.
   * @returns {Promise<string>}
   * @private
   */
  async _stopAndTranscribe({ trigger, deliverViaEvent }) {
    // Single guarded entry: only act while recording. Silent no-op otherwise so a
    // late timer / double click cannot destroy an in-flight transcription.
    if (this.state !== 'recording') return '';

    // CRITICAL: clear both auto-stop timers synchronously, before any await, so a
    // pending timer can't re-enter this path during `processing`.
    this._clearTimers();

    // Capture duration BEFORE releasing resources (release nulls recordingStartedAt).
    const durationSeconds = this.getRecordingDurationSeconds();

    // Assemble the final blob (local var — survives resource release), then release
    // ALL live resources immediately so the mic indicator goes dark during transcribe.
    const audioBlob = await this._finalizeBlob();
    this._releaseAudioResources();

    this.state = 'processing';
    this.emitStateChange();

    try {
      const text = await this.transcribe(audioBlob, durationSeconds);
      this.state = 'idle';
      this.emitStateChange();
      if (deliverViaEvent && this.eventBus) {
        this.eventBus.emit('audio:auto-stop', { text, trigger });
      }
      return text;
    } catch (error) {
      const wasCancelled = this._cancelled;
      this._cancelled = false;
      // A deliberate cancel already moved us to idle in cancelRecording().
      if (wasCancelled) return '';

      this.state = 'idle';
      this.emitStateChange();
      if (deliverViaEvent) {
        if (this.eventBus) {
          this.eventBus.emit('audio:auto-stop-failed', {
            message: error.message || 'Transcription failed',
            trigger
          });
        }
        return '';
      }
      throw error;
    }
  }

  /**
   * Stop the MediaRecorder and resolve the assembled audio blob once its final
   * `dataavailable` has flushed.
   * @returns {Promise<Blob>}
   * @private
   */
  _finalizeBlob() {
    return new Promise((resolve) => {
      if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
        resolve(new Blob(this.audioChunks, { type: this.mimeType }));
        return;
      }
      this.mediaRecorder.onstop = () => {
        resolve(new Blob(this.audioChunks, { type: this.mimeType }));
      };
      this.mediaRecorder.stop();
    });
  }

  /**
   * Cancel recording or an in-flight transcription without delivering a transcript.
   * Aborts the transcribe fetch (if any) and releases all resources immediately.
   */
  cancelRecording() {
    if (this.state === 'idle') return;

    this._cancelled = true;
    this._clearTimers();

    if (this._abortController) {
      try { this._abortController.abort(); } catch { /* already settled */ }
    }

    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      try { this.mediaRecorder.stop(); } catch { /* already stopped */ }
    }

    this._releaseAudioResources();
    this.state = 'idle';
    this.emitStateChange();
  }

  /**
   * Set up the live waveform / silence-detection audio graph from the mic stream.
   * The analyser is never connected to `destination` (no audio playthrough).
   * @param {MediaStream} stream
   * @returns {Promise<void>}
   * @private
   */
  async _setupAudioGraph(stream) {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return; // No Web Audio — recording still works, just no live waveform.

    this.audioContext = new Ctor();
    // iOS starts the context suspended until a user gesture resumes it; a suspended
    // context yields a flat waveform and instant false-silence. Resume before reading.
    if (this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume();
      } catch {
        this._releaseAudioResources();
        const err = new Error('Could not access microphone audio');
        err.name = 'AudioContextError';
        throw err;
      }
    }

    this.sourceNode = this.audioContext.createMediaStreamSource(stream);
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = FFT_SIZE;
    this.analyser.smoothingTimeConstant = 0.8;
    this.sourceNode.connect(this.analyser);
  }

  /**
   * Start the silence + max-duration auto-stop timers.
   * @private
   */
  _startTimers() {
    this._lastLoudAt = this.recordingStartedAt || Date.now();
    this._speaking = false;

    this._maxTimer = setTimeout(() => {
      this._stopAndTranscribe({ trigger: 'max', deliverViaEvent: true });
    }, MAX_DURATION_MS);

    this._silenceTimer = setInterval(() => {
      const rms = this._currentRms();
      const now = Date.now();

      if (rms >= SILENCE_RMS_THRESHOLD) {
        this._speaking = true;
        this._lastLoudAt = now;
      } else if (this._speaking && rms >= SILENCE_RMS_RELEASE) {
        // Hysteresis: soft continuation of speech still counts as loud.
        this._lastLoudAt = now;
      } else {
        this._speaking = false;
      }

      if (now - this._lastLoudAt >= SILENCE_TIMEOUT_MS) {
        this._stopAndTranscribe({ trigger: 'silence', deliverViaEvent: true });
      }
    }, SILENCE_SAMPLE_MS);
  }

  /**
   * Clear both auto-stop timers. Safe to call repeatedly.
   * @private
   */
  _clearTimers() {
    if (this._maxTimer) {
      clearTimeout(this._maxTimer);
      this._maxTimer = null;
    }
    if (this._silenceTimer) {
      clearInterval(this._silenceTimer);
      this._silenceTimer = null;
    }
  }

  /**
   * Current time-domain RMS amplitude in `[0, 1]` (0 if the graph is torn down).
   * @returns {number}
   * @private
   */
  _currentRms() {
    if (!this.analyser) return 0;
    try {
      const data = new Uint8Array(this.analyser.fftSize);
      this.analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) {
        const v = (data[i] - 128) / 128;
        sum += v * v;
      }
      return Math.sqrt(sum / data.length);
    } catch {
      return 0;
    }
  }

  /**
   * Send audio to backend for transcription. Wrapped in an AbortController so
   * {@link AudioRecordingService#cancelRecording} can abort an in-flight request.
   * @param {Blob} audioBlob - Recorded audio blob
   * @param {number} durationSeconds - Captured recording duration (resources already released)
   * @returns {Promise<string>} Transcribed text
   * @private
   */
  async transcribe(audioBlob, durationSeconds) {
    const formData = new FormData();
    // Use appropriate extension based on mime type
    const extension = this.mimeType.includes('mp4') ? 'mp4' : 'webm';
    formData.append('audio', audioBlob, `recording.${extension}`);
    formData.append('audioDurationSeconds', String(durationSeconds));

    this._abortController = new AbortController();
    const timeoutId = setTimeout(() => {
      try { this._abortController?.abort(); } catch { /* already settled */ }
    }, TRANSCRIBE_TIMEOUT_MS);

    let response;
    try {
      response = await fetch(`${API_URL}/transcription`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
        signal: this._abortController.signal
      });
    } catch (error) {
      if (error.name === 'AbortError' && !this._cancelled) {
        throw new Error('Transcription timed out. Please try again.');
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
      this._abortController = null;
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (response.status === 429 || errorData.code === 'TRANSCRIPTION_LIMIT_REACHED') {
        this.applyQuotaPayload({
          available: false,
          message: errorData.error || 'Voice transcription limit reached. Try again later.',
          quota: errorData.metadata || null
        });
      }
      throw new Error(errorData.error || 'Transcription failed');
    }

    const result = await response.json();
    if (result.metadata?.quota) {
      this.applyQuotaPayload({
        available: result.metadata.quota.allowed,
        message: result.metadata.transcriptionLimitMessage || null,
        quota: result.metadata.quota
      });
    }
    return result.success && result.data?.text ? result.data.text : '';
  }

  /**
   * Refresh current transcription quota state from the backend.
   * @returns {Promise<void>}
   */
  async refreshQuotaStatus() {
    try {
      const response = await fetchWithTimeout(`${API_URL}/transcription/status`, {
        method: 'GET',
        credentials: 'include'
      }, 10000);

      if (!response.ok) return;

      const result = await response.json();
      if (result.success && result.data) {
        this.applyQuotaPayload(result.data);
      }
    } catch {
      // Quota status is advisory UI state. A failed status check should not
      // remove the user's ability to try recording.
    }
  }

  /**
   * Release ALL live media resources: stream tracks, AudioContext, analyser nodes,
   * recorder, and buffered chunks. Idempotent. Does NOT touch transcription state.
   * @private
   */
  _releaseAudioResources() {
    if (this.sourceNode) {
      try { this.sourceNode.disconnect(); } catch { /* already disconnected */ }
      this.sourceNode = null;
    }
    if (this.analyser) {
      try { this.analyser.disconnect(); } catch { /* already disconnected */ }
      this.analyser = null;
    }
    if (this.audioContext) {
      try { this.audioContext.close(); } catch { /* already closed */ }
      this.audioContext = null;
    }
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
    this.mediaRecorder = null;
    this.audioChunks = [];
    this.recordingStartedAt = null;
  }

  /**
   * Get elapsed recording duration in seconds.
   * @returns {number}
   * @private
   */
  getRecordingDurationSeconds() {
    if (!this.recordingStartedAt) return 1;
    return Math.max(1, Math.ceil((Date.now() - this.recordingStartedAt) / 1000));
  }

  /**
   * Apply backend quota payload and notify the input component.
   * @param {Object} payload
   * @private
   */
  applyQuotaPayload(payload) {
    this.quota = {
      available: Boolean(payload.available),
      message: payload.message || null,
      data: payload.quota || null
    };
    if (this.eventBus) {
      this.eventBus.emit('audio:quota-changed', this.quota);
    }
  }

  /**
   * Destroy service and release all resources.
   * Call this when navigating away or unmounting the component.
   */
  destroy() {
    if (this.state !== 'idle') {
      this.cancelRecording();
    } else {
      this._releaseAudioResources();
    }
    this._clearTimers();
    this.state = 'idle';
  }

  /**
   * Emit state change event
   * @private
   */
  emitStateChange() {
    if (this.eventBus) {
      this.eventBus.emit('audio:state-changed', { state: this.state });
    }
  }
}
